import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { MapPin, Phone, Mail, MessageCircle } from 'lucide-react';
import SectionHeading from '@/components/SectionHeading';
import Field, { inputCls } from '@/components/Field';
import { Reveal } from '@/components/Reveal';
import { useLanguage } from '@/context/LanguageContext';
import { API } from '@/lib/api';

const EMPTY = { name: '', phone: '', email: '', message: '' };

export default function Contact() {
  const { t } = useLanguage();
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const offices = t('contact.offices.items');

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.message) { toast.error(t('common.required')); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success(t('contact.form.success'));
      setForm(EMPTY);
    } catch { toast.error(t('contact.form.error')); } finally { setLoading(false); }
  };

  return (
    <div className="pt-28" data-testid="contact-page">
      <section className="mx-auto max-w-7xl px-5 pt-8 lg:px-8">
        <SectionHeading eyebrow="contact.eyebrow" title="contact.title" sub="contact.sub" align="center" />
      </section>

      <section className="mx-auto grid max-w-7xl gap-6 px-5 py-14 lg:grid-cols-12 lg:px-8">
        <Reveal className="lg:col-span-7">
          <form onSubmit={submit} className="rounded-[2rem] border border-brand-line bg-white p-8 shadow-[0_40px_80px_-40px_rgba(15,23,42,0.2)]" data-testid="contact-form">
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label={t('contact.form.name')} testid="contact-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              <Field label={t('contact.form.phone')} testid="contact-phone-input" dir="ltr" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
              <div className="sm:col-span-2">
                <Field label={t('contact.form.email')} testid="contact-email-input" type="email" dir="ltr" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              </div>
              <div className="sm:col-span-2">
                <label className="block">
                  <span className="mb-1.5 block text-xs font-bold text-brand-steel">{t('contact.form.message')}</span>
                  <textarea rows="5" data-testid="contact-message-input" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className={inputCls} />
                </label>
              </div>
            </div>
            <button type="submit" disabled={loading} data-testid="contact-submit-btn" className="mt-6 w-full rounded-full bg-brand-blue py-3.5 text-sm font-bold text-white transition-all duration-300 hover:bg-brand-blueHover hover:shadow-[0_16px_36px_-12px_rgba(0,82,255,0.6)] disabled:opacity-60">
              {loading ? t('common.submitting') : t('contact.form.submit')}
            </button>
          </form>
        </Reveal>

        <div className="flex flex-col gap-5 lg:col-span-5">
          <Reveal delay={0.1}>
            <div className="rounded-[2rem] border border-brand-line bg-white p-7" data-testid="contact-offices">
              <h3 className="font-mono text-[11px] font-bold uppercase tracking-widest text-brand-dim">{t('contact.offices.title')}</h3>
              <ul className="mt-5 space-y-4">
                {offices.map((o) => (
                  <li key={o.city} className="flex items-start gap-3">
                    <span className="mt-0.5 grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-brand-blue/8 text-brand-blue border border-brand-blue/15"><MapPin className="h-4 w-4" /></span>
                    <span>
                      <span className="block text-sm font-extrabold text-brand-ink">{o.city}</span>
                      <span className="block text-xs text-brand-steel">{o.addr}</span>
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>

          <Reveal delay={0.18}>
            <div className="rounded-[2rem] bg-brand-ink p-7" data-testid="contact-channels">
              <div className="space-y-4">
                <a href={`tel:${t('settings.phone').replace(/\s/g, '')}`} className="flex items-center gap-3 text-white transition-opacity hover:opacity-80" data-testid="contact-channel-phone">
                  <span className="grid h-10 w-10 place-items-center rounded-xl bg-white/10"><Phone className="h-4 w-4 text-brand-sky" /></span>
                  <span>
                    <span className="block font-mono text-[10px] uppercase tracking-widest text-brand-dim">{t('contact.channels.phone')}</span>
                    <span className="block font-mono text-sm font-bold" dir="ltr">{t('settings.phone')}</span>
                  </span>
                </a>
                <a href={`mailto:${t('settings.email')}`} className="flex items-center gap-3 text-white transition-opacity hover:opacity-80" data-testid="contact-channel-email">
                  <span className="grid h-10 w-10 place-items-center rounded-xl bg-white/10"><Mail className="h-4 w-4 text-brand-sky" /></span>
                  <span>
                    <span className="block font-mono text-[10px] uppercase tracking-widest text-brand-dim">{t('contact.channels.email')}</span>
                    <span className="block font-mono text-sm font-bold" dir="ltr">{t('settings.email')}</span>
                  </span>
                </a>
                <a href={`https://wa.me/${t('settings.whatsapp')}`} target="_blank" rel="noreferrer" className="flex items-center gap-3 text-white transition-opacity hover:opacity-80" data-testid="contact-channel-whatsapp">
                  <span className="grid h-10 w-10 place-items-center rounded-xl bg-white/10"><MessageCircle className="h-4 w-4 text-brand-emerald" /></span>
                  <span>
                    <span className="block font-mono text-[10px] uppercase tracking-widest text-brand-dim">{t('contact.channels.whatsapp')}</span>
                    <span className="block text-sm font-bold">{t('contact.channels.whatsappV')}</span>
                  </span>
                </a>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
